import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { SelectForm } from '../../../components/select-form/select-form';
import { Spinner } from '../../../components/spinner/spinner';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { ConfigBinaryProperty, ConfigInputProperty, ConfigProperty, ConfigSection } from '../config-property';
import { DeliveryConfig, DeliveryConfigType } from '../../../clients/quipli-service/delivery/delivery';
import { Radio } from '../../../components/radio/radio';
import { debounce } from 'lodash';
import usePrevious from '../../../hooks/use-previous';
import { toast } from '../../../util/toasts';
import deepEqual from 'deep-equal';
import { QuipliLink } from '../../../components/link/quipli-link';
import { Typography } from '../../../components/typography/typography';
import { Tooltip } from '../../../components/tooltip/tooltip';
import Icon from '../../../components/icons/icon';

const DELIVERY_OPTIONS = [
  {
    value: DeliveryConfigType.PerProduct,
    label: 'Per Product Level (Calculation will vary per product. Can be calculated based on distance or a flat fee).',
  },
  {
    value: DeliveryConfigType.Global,
    label: 'Global level (The same calculation applies to all products. Can be calculated on distance or a flat fee).',
  },
  {
    value: DeliveryConfigType.QuotedAfterOrder,
    label: 'Delivery will be quoted after order is placed (Delivery will be determined by admin for each order).',
  },
  {
    value: DeliveryConfigType.ZipCode,
    label: 'Delivery Rate by Zip Codes (Rates will be determined based on customer delivery address zip code.',
  },
];

export const DeliveryChargesConfig = () => {
  const { getDeliveryConfiguration, updateDeliveryConfiguration } = useAPI();

  const [config, setConfig] = useState<DeliveryConfig>();
  const prevConfig = usePrevious(config);

  const doUpdate = (_config: DeliveryConfig) => {
    updateDeliveryConfiguration(_config).catch(() => {
      toast('Failed to update delivery configuration', 'error');
    });
  };

  const debouncedDoUpdate = useCallback(
    debounce(() => config && doUpdate(config), 500),
    [config],
  );

  useEffect(() => {
    getDeliveryConfiguration()
      .then(setConfig)
      .catch(() => {
        toast('Failed to load delivery configuration', 'error');
      });
  }, []);

  useEffect(() => {
    if (prevConfig && config && !deepEqual(prevConfig, config)) {
      debouncedDoUpdate();
    }
  }, [config]);

  return config ? (
    <ConfigSection>
      <ConfigProperty property="Delivery Charge Options">
        <SelectForm
          value={DELIVERY_OPTIONS.find((op) => op.value === config.type)}
          options={DELIVERY_OPTIONS}
          onChange={(op) => op && setConfig((prev) => prev && { ...prev, type: op.value })}
        />
      </ConfigProperty>

      {config.type !== undefined && (
        <>
          {config.type === DeliveryConfigType.Global && <GlobalConfiguration config={config} setConfig={setConfig} />}
          {config.type === DeliveryConfigType.PerProduct && (
            <PerProductConfiguration config={config} setConfig={setConfig} />
          )}
          {config.type !== DeliveryConfigType.ZipCode && (
            <ConfigBinaryProperty
              property="One Way Trip"
              description="Enable/Disable One Way Trip."
              value={config.allowOneWay}
              onChange={(value) => setConfig((prev) => prev && { ...prev, allowOneWay: value })}
            />
          )}
        </>
      )}

      <ConfigBinaryProperty
        property="Apply Taxes To Delivery Charges"
        value={config.taxable}
        onChange={(value) => setConfig((prev) => prev && { ...prev, taxable: value })}
      />
    </ConfigSection>
  ) : (
    <Spinner />
  );
};

const GlobalConfiguration = ({
  config,
  setConfig,
}: {
  config: DeliveryConfig;
  setConfig: Dispatch<SetStateAction<DeliveryConfig | undefined>>;
}) => {
  if (!config) return null;

  return (
    <>
      <ConfigBinaryProperty
        property="Enable/Disable delivery calculation using Google API:"
        description="Allows to use google API to calculate the delivery charges using Delivery Cost Per Mile or Delivery Cost Per Minute option as cost reference."
        value={config.useGoogleCalculation}
        onChange={(value) => setConfig((prev) => prev && { ...prev, useGoogleCalculation: value })}
      />

      {config.useGoogleCalculation ? (
        <>
          <ConfigProperty property="Distance Measure Unit">
            <div className="flex gap-2">
              <Radio
                label="Miles"
                isChecked={config.googleCalculation.measure === 'Mile'}
                onChange={(isChecked) =>
                  isChecked &&
                  setConfig(
                    (prev) =>
                      prev && {
                        ...prev,
                        googleCalculation: {
                          ...prev.googleCalculation,
                          measure: 'Mile',
                        },
                      },
                  )
                }
              />
              <Radio
                label="Kilometers"
                isChecked={config.googleCalculation.measure === 'Kilometer'}
                onChange={(isChecked) =>
                  isChecked &&
                  setConfig(
                    (prev) =>
                      prev && {
                        ...prev,
                        googleCalculation: {
                          ...prev.googleCalculation,
                          measure: 'Kilometer',
                        },
                      },
                  )
                }
              />
              <Radio
                label="Minutes"
                isChecked={config.googleCalculation.measure === 'Minute'}
                onChange={(isChecked) =>
                  isChecked &&
                  setConfig(
                    (prev) =>
                      prev && {
                        ...prev,
                        googleCalculation: {
                          ...prev.googleCalculation,
                          measure: 'Minute',
                        },
                      },
                  )
                }
              />
            </div>
          </ConfigProperty>

          <ConfigInputProperty
            property="Delivery Base Cost"
            description="Define the base cost for a one way loaded trip."
            type="number"
            value={config.googleCalculation.baseCost}
            onChange={(value: number) =>
              setConfig(
                (prev) =>
                  prev && {
                    ...prev,
                    googleCalculation: {
                      ...prev.googleCalculation,
                      baseCost: value >= 0 ? value : 0,
                    },
                  },
              )
            }
          />

          <ConfigInputProperty
            property={`${config.googleCalculation.measure}s included in Delivery Base Cost`}
            description={`Define the ${
              config.googleCalculation.measure === 'Mile'
                ? 'mileage'
                : config.googleCalculation.measure.toLocaleLowerCase()
            } radius (from your store location) included in the base cost.`}
            type="number"
            value={config.googleCalculation.includedUnits}
            onChange={(value: number) =>
              setConfig(
                (prev) =>
                  prev && {
                    ...prev,
                    googleCalculation: {
                      ...prev.googleCalculation,
                      includedUnits: value >= 0 ? value : 0,
                    },
                  },
              )
            }
          />

          <ConfigInputProperty
            property={`Per ${config.googleCalculation.measure} Cost`}
            description={`Define the additional cost per one way loaded ${config.googleCalculation.measure.toLocaleLowerCase()} for deliveries beyond the base cost radius.`}
            type="currency"
            value={config.googleCalculation.perUnitCost}
            onChange={(value: number) =>
              setConfig(
                (prev) =>
                  prev && {
                    ...prev,
                    googleCalculation: {
                      ...prev.googleCalculation,
                      perUnitCost: value >= 0 ? value : 0,
                    },
                  },
              )
            }
          />
        </>
      ) : (
        <>
          <ConfigInputProperty
            property="Flat Delivery Charges"
            description="Define the flat rate for a one way trip (this amount will be doubled for round trip deliveries)."
            type="number"
            value={config.flatRate}
            onChange={(value: number) => setConfig((prev) => prev && { ...prev, flatRate: value >= 0 ? value : 0 })}
          />
        </>
      )}

      <WillCallAtProductLevelConfig config={config} setConfig={setConfig} />
    </>
  );
};

const PerProductConfiguration = ({
  config,
  setConfig,
}: {
  config: DeliveryConfig;
  setConfig: Dispatch<SetStateAction<DeliveryConfig | undefined>>;
}) => {
  if (!config) return null;

  return (
    <>
      <GlobalConfiguration config={config} setConfig={setConfig} />
      <ConfigBinaryProperty
        property="Charge One rate for Multi-Product Orders"
        description="When enabled, orders that have more than one product will be charged only one delivery fee. If products have unique rate, then order will get the highest delivery rate."
        value={config.useMaxForPerProductDeliveryCalculation}
        onChange={(value) => setConfig((prev) => prev && { ...prev, useMaxForPerProductDeliveryCalculation: value })}
      />
    </>
  );
};

const WillCallAtProductLevelConfig = ({
  config,
  setConfig,
}: {
  config: DeliveryConfig;
  setConfig: Dispatch<SetStateAction<DeliveryConfig | undefined>>;
}) => {
  if (!config) return null;

  return (
    <ConfigBinaryProperty
      property="Enable/Disable Delivery Free Text Option At Product Level"
      helper={
        <Tooltip
          tooltip={
            <QuipliLink to="labels?keys=L_DELIVERY_WILL_CALL" target="_blank">
              <div className="p-[12px]">
                <Typography type="text-sm" className="!text-quipli-white-50 underline">
                  AKA "Will Call". Click to change label.
                </Typography>
              </div>
            </QuipliLink>
          }
          type="black"
        >
          <Icon icon="InfoCircle" stroke="var(--link-100)" />
        </Tooltip>
      }
      value={config.allowWillCallAtProductLevel}
      onChange={(value) => setConfig((prev) => prev && { ...prev, allowWillCallAtProductLevel: value })}
    />
  );
};
